import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Users, Circle, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { getCurrentUserId } from '../utils/currentUser';
import { isRealAvatar } from '../utils/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

const API = process.env.REACT_APP_BACKEND_URL;

// Resolve real user identity from localStorage (populated at login). This
// replaces the previous hardcoded `name: 'Principal Designer'` which made
// every user show up under the same name.
const getCurrentUser = () => {
  try {
    return {
      id: getCurrentUserId(),
      name: localStorage.getItem('userName') || localStorage.getItem('userEmail') || 'You',
      role: localStorage.getItem('userRole') || 'Team Member',
      avatar: localStorage.getItem('userAvatar') || null,
    };
  } catch {
    return { id: null, name: 'You', role: 'Team Member', avatar: null };
  }
};

// Page name mapping for display
const PAGE_NAMES = {
  '/': 'My Desk',
  '/dashboard': 'My Desk',
  '/projects': 'Projects',
  '/knowledge': 'Library',
  '/corner': 'Corner Desk',
  '/clips': 'Clips',
  '/team': 'Team',
  '/financials': 'Financials',
  '/documents': 'Documents',
  '/blueprints': 'Blueprints',
  '/notifications': 'Notifications',
  '/settings': 'Settings',
  '/admin': 'Admin',
  '/resources': 'Resources',
  '/glossary': 'Glossary'
};

const getPageName = (path) => {
  if (!path) return 'Unknown';
  
  // Check exact matches first
  if (PAGE_NAMES[path]) return PAGE_NAMES[path];
  
  // Check for dynamic routes
  if (path.startsWith('/work-tables/')) return 'Work Table';
  if (path.startsWith('/projects/')) return 'Project';
  if (path.startsWith('/documents/')) return 'Document';
  if (path.startsWith('/drawing-room/')) return 'Drawing Room';
  if (path.startsWith('/clips/')) return 'Clip Detail';
  if (path.startsWith('/knowledge/')) return 'Library Item';
  
  return 'KEY-MADE';
};

const getStatusColor = (status) => {
  switch (status) {
    case 'online': return 'bg-green-500';
    case 'away': return 'bg-yellow-500';
    case 'busy': return 'bg-red-500';
    default: return 'bg-gray-400';
  }
};

const getUserColor = (userId) => {
  // Generate consistent color based on user ID
  const colors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
  ];
  const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

export default function WhosOnline({ darkMode = false, compact = false }) {
  const wsRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  // Resolve real user identity at mount time (auth fields are set by the
  // login flow into localStorage). Stored in a ref so identity is stable
  // across re-renders and effect cleanup never sees a stale snapshot.
  const currentUserRef = useRef(getCurrentUser());
  const CURRENT_USER = currentUserRef.current;
  
  // Connect to global presence WebSocket
  useEffect(() => {
    // Determine WebSocket protocol based on backend URL or page protocol
    const wsProtocol = (API || '').startsWith('https') ? 'wss:' : (window.location.protocol === 'https:' ? 'wss:' : 'ws:');
    const wsHost = API?.replace(/^https?:\/\//, '') || window.location.host;
    const currentPage = window.location.pathname;
    const pageName = getPageName(currentPage);
    
    // Don't connect if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }
    
    const wsUrl = (() => {
      // Round 4 Phase 1c: JWT required as query param. user_id from query is
      // IGNORED server-side; identity is bound from the JWT claim. Display
      // fields (user_name, role, page) are still passed for the presence card.
      const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
      if (!token) return null;
      return `${wsProtocol}//${wsHost}/ws/presence?token=${encodeURIComponent(token)}&user_name=${encodeURIComponent(CURRENT_USER.name)}&role=${encodeURIComponent(CURRENT_USER.role)}&current_page=${encodeURIComponent(currentPage)}&current_page_name=${encodeURIComponent(pageName)}`;
    })();
    if (!wsUrl) return;
    
    // Fallback to REST API polling if WebSocket not available.
    // Round 5 fix: backend `/presence/online` now MERGES the WS-backed
    // and REST-backed presence stores by user_id, so this no longer
    // overwrites the live WS-pushed list with stale REST-only data.
    const pollPresence = async () => {
      try {
        const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
        if (!token) return;
        const response = await axios.get(`${API}/api/presence/online`);
        setOnlineUsers(response.data.users || []);
      } catch (err) {
        console.error('Failed to poll presence:', err);
      }
    };
    
    // Send heartbeat via REST API.
    // Round 5 fix: this now runs on a periodic interval (every 30s) — not
    // just once at mount — so the REST-backed `rest_presence` store stays
    // fresh and never expires (60s TTL) while the user is active. That
    // eliminates the "two stores disagree → flicker" root cause.
    const sendHeartbeat = async () => {
      try {
        const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
        if (!token) return;
        await axios.post(`${API}/api/presence/heartbeat`, null, {
          params: {
            user_name: CURRENT_USER.name,
            role: CURRENT_USER.role,
            current_page: window.location.pathname,
            current_page_name: getPageName(window.location.pathname),
            avatar: CURRENT_USER.avatar || undefined,
          },
        });
      } catch (err) {
        // Silently fail heartbeat
      }
    };
    
    // Send initial heartbeat
    sendHeartbeat();
    
    const connectWebSocket = () => {
      // Prevent multiple simultaneous connection attempts
      if (wsRef.current?.readyState === WebSocket.CONNECTING) {
        return;
      }
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        setIsConnected(true);
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'global_presence') {
          setOnlineUsers(data.users || []);
        }
      };
      
      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;
        // Reconnect after 10 seconds (increased from 5)
        setTimeout(connectWebSocket, 10000);
      };
      
      ws.onerror = () => {
        setIsConnected(false);
        // Fallback to polling when WebSocket fails
        pollPresence();
      };
    };
    
    connectWebSocket();
    
    // Periodic polling as fallback (every 30 seconds - reduced frequency)
    const pollInterval = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        pollPresence();
      }
    }, 30000);
    
    // Initial poll
    pollPresence();
    
    // Round 5: keep REST `rest_presence` fresh (TTL 60s server-side) by
    // pinging every 30s regardless of WS state. Cheap, ensures `/online`
    // merge always includes the current user.
    const restHeartbeatInterval = setInterval(sendHeartbeat, 30000);

    // Send page updates when URL changes
    const handlePopState = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const newPage = window.location.pathname;
        wsRef.current.send(JSON.stringify({
          type: 'update_page',
          current_page: newPage,
          current_page_name: getPageName(newPage)
        }));
      }
      // Also bump REST so heartbeat carries the new page
      sendHeartbeat();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    // Heartbeat every 60 seconds (reduced from 30)
    const heartbeatInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'heartbeat' }));
      }
    }, 60000);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearInterval(heartbeatInterval);
      clearInterval(pollInterval);
      clearInterval(restHeartbeatInterval);
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- CURRENT_USER is a stable ref-backed snapshot
  
  // Update presence when navigating (for SPA navigation)
  const updateCurrentPage = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const newPage = window.location.pathname;
      wsRef.current.send(JSON.stringify({
        type: 'update_page',
        current_page: newPage,
        current_page_name: getPageName(newPage)
      }));
    }
  }, []);
  
  // Expose updateCurrentPage for parent components to call on navigation
  useEffect(() => {
    window.updatePresencePage = updateCurrentPage;
    return () => {
      delete window.updatePresencePage;
    };
  }, [updateCurrentPage]);
  
  const otherUsers = onlineUsers.filter(u => u.user_id !== CURRENT_USER.id);
  const onlineCount = onlineUsers.length;
  
  if (compact) {
    // Compact version for sidebar or header
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className={`flex items-center gap-2 ${darkMode ? 'text-gray-300 hover:bg-[#2A2A2A]' : ''}`}
          >
            <div className="relative">
              <Users className="w-4 h-4" />
              {onlineCount > 0 && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full text-[9px] text-white flex items-center justify-center font-bold">
                  {onlineCount > 9 ? '9+' : onlineCount}
                </span>
              )}
            </div>
            <span className="text-xs">{onlineCount} online</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className={`w-72 p-0 ${darkMode ? 'bg-[#1F1F1F] border-[#3A3A3A]' : ''}`}
          align="end"
        >
          <div className={`p-3 border-b ${darkMode ? 'border-[#3A3A3A]' : ''}`}>
            <h4 className={`font-medium text-sm flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
              <Users className="w-4 h-4" />
              Who&apos;s Online
              <span className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`}>
                {onlineCount}
              </span>
            </h4>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {onlineUsers.length === 0 ? (
              <div className={`p-4 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
                No one else is online
              </div>
            ) : (
              <div className="py-1">
                {onlineUsers.map(user => (
                  <div 
                    key={user.user_id}
                    className={`px-3 py-2 flex items-center gap-3 ${darkMode ? 'hover:bg-[#2A2A2A]' : 'hover:bg-gray-50'}`}
                  >
                    <div className="relative">
                      {isRealAvatar(user.avatar) ? (
                        <>
                          <img
                            src={user.avatar}
                            alt={user.user_name || ''}
                            className="w-8 h-8 rounded-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const sib = e.currentTarget.nextElementSibling;
                              if (sib) sib.style.display = 'flex';
                            }}
                            data-testid="whos-online-avatar-compact"
                          />
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                            style={{ backgroundColor: user.color || getUserColor(user.user_id), display: 'none' }}
                            data-testid="whos-online-avatar-compact-fallback"
                          >
                            {user.user_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        </>
                      ) : (
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                          style={{ backgroundColor: user.color || getUserColor(user.user_id) }}
                        >
                          {user.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 ${darkMode ? 'border-[#1F1F1F]' : 'border-white'} ${getStatusColor(user.status)}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : ''}`}>
                        {user.user_name}
                        {user.user_id === CURRENT_USER.id && (
                          <span className={`text-xs ml-1 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>(you)</span>
                        )}
                      </p>
                      <p className={`text-xs flex items-center gap-1 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
                        <MapPin className="w-3 h-3" />
                        {user.current_page_name || 'Unknown'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  
  // Full panel version for sidebar
  return (
    <div className={`rounded-lg border ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-gray-200'}`}>
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full px-4 py-3 flex items-center justify-between ${darkMode ? 'hover:bg-[#2A2A2A]' : 'hover:bg-gray-50'} rounded-t-lg transition-colors`}
      >
        <div className="flex items-center gap-2">
          <div className="relative">
            <Users className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} />
            <div className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          </div>
          <span className={`font-medium ${darkMode ? 'text-white' : ''}`}>Who&apos;s Online</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`}>
            {onlineCount}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        ) : (
          <ChevronDown className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        )}
      </button>
      
      {isExpanded && (
        <div className={`border-t ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-100'}`}>
          {onlineUsers.length === 0 ? (
            <div className={`p-4 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
              Connecting...
            </div>
          ) : (
            <div className="py-2 max-h-48 overflow-y-auto">
              {onlineUsers.map(user => (
                <div 
                  key={user.user_id}
                  className={`px-4 py-2 flex items-center gap-3 ${darkMode ? 'hover:bg-[#2A2A2A]' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <div className="relative">
                    {isRealAvatar(user.avatar) ? (
                      <>
                        <img
                          src={user.avatar}
                          alt={user.user_name || ''}
                          className="w-9 h-9 rounded-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const sib = e.currentTarget.nextElementSibling;
                            if (sib) sib.style.display = 'flex';
                          }}
                          data-testid="whos-online-avatar-full"
                        />
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium"
                          style={{ backgroundColor: user.color || getUserColor(user.user_id), display: 'none' }}
                          data-testid="whos-online-avatar-full-fallback"
                        >
                          {user.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      </>
                    ) : (
                      <div 
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium"
                        style={{ backgroundColor: user.color || getUserColor(user.user_id) }}
                      >
                        {user.user_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 ${darkMode ? 'border-[#1A1A1A]' : 'border-white'} ${getStatusColor(user.status)}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : ''}`}>
                      {user.user_name}
                      {user.user_id === CURRENT_USER.id && (
                        <span className={`text-xs ml-1 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>(you)</span>
                      )}
                    </p>
                    <div className="flex items-center gap-1">
                      <Circle className={`w-2 h-2 ${user.status === 'online' ? 'fill-green-500 text-green-500' : 'fill-gray-400 text-gray-400'}`} />
                      <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
                        {user.current_page_name || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
